import { useQuery } from '@tanstack/react-query';
import { supabase, tables } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { ErrorCategory } from '@/utils/errorHandler';
import { useMemo } from 'react';

export interface OverduePayment {
  id: string;
  student_name: string;
  family_name: string;
  amount_due: number;
  days_overdue: number;
  last_payment_date: string | null;
}

export interface PaymentHealthData {
  overdue_payments: OverduePayment[];
  total_overdue_amount: number;
  count: number;
}

/**
 * Hook to fetch payment health data - overdue payments and amounts
 */
export const usePaymentHealth = () => {
  const { user } = useAuth();
  const errorHandler = useErrorHandler('usePaymentHealth');
  
  const organizationId = useMemo(() => {
    return user?.user_metadata?.organization_id as string | undefined;
  }, [user?.user_metadata?.organization_id]);

  return useQuery({
    queryKey: ['paymentHealth', organizationId] as const,
    queryFn: async (): Promise<PaymentHealthData> => {
      if (!organizationId) {
        throw errorHandler.handleError(
          new Error('No organization ID found'),
          'fetchPaymentHealth',
          { userId: user?.id },
          false
        );
      }

      try {
        // Get overdue invoices with family details
        const { data: overdueInvoices, error: invoicesError } = await tables
          .invoices()
          .select(`
            id,
            family_id,
            total_cents,
            due_date,
            status,
            families!inner(name)
          `)
          .eq('organization_id', organizationId)
          .or('status.eq.overdue,and(status.eq.unpaid,due_date.lt.today())')
          .order('due_date', { ascending: true });

        if (invoicesError) throw invoicesError;

        if (!overdueInvoices || overdueInvoices.length === 0) {
          return { overdue_payments: [], total_overdue_amount: 0, count: 0 };
        }

        // Get student names for each family
        const familyIds = overdueInvoices.map(invoice => invoice.family_id);
        const { data: students, error: studentsError } = await tables
          .students()
          .select('id, first_name, last_name, family_id')
          .eq('organization_id', organizationId)
          .in('family_id', familyIds);

        if (studentsError) throw studentsError;

        // Get last payment dates for families
        const { data: lastPayments, error: paymentsError } = await tables
          .payments()
          .select('family_id, paid_at')
          .eq('organization_id', organizationId)
          .in('family_id', familyIds)
          .not('paid_at', 'is', null)
          .order('paid_at', { ascending: false });

        if (paymentsError) throw paymentsError;

        // Build overdue payments array
        const overduePayments: OverduePayment[] = overdueInvoices.map(invoice => {
          const family = invoice.families;
          const familyStudents = students?.filter(s => s.family_id === invoice.family_id) || [];
          const studentName = familyStudents.length === 1 
            ? `${familyStudents[0].first_name} ${familyStudents[0].last_name}`
            : familyStudents.length > 1 
            ? `${familyStudents[0].first_name} ${familyStudents[0].last_name} (+${familyStudents.length - 1} more)`
            : 'Unknown Student';

          const lastPayment = lastPayments?.find(p => p.family_id === invoice.family_id);
          const dueDate = new Date(invoice.due_date);
          const today = new Date();
          const daysOverdue = Math.max(0, Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24)));

          return {
            id: invoice.id,
            student_name: studentName,
            family_name: family?.name || 'Unknown Family',
            amount_due: invoice.total_cents / 100, // Convert cents to dollars
            days_overdue: daysOverdue,
            last_payment_date: lastPayment?.paid_at || null,
          };
        });

        const totalOverdueAmount = overduePayments.reduce(
          (sum, payment) => sum + payment.amount_due, 
          0
        );

        return {
          overdue_payments: overduePayments,
          total_overdue_amount: totalOverdueAmount,
          count: overduePayments.length,
        };
      } catch (error) {
        throw errorHandler.handleError(
          error,
          'fetchPaymentHealth',
          { 
            userId: user?.id,
            organizationId,
            category: ErrorCategory.DATABASE
          },
          false
        );
      }
    },
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep unused data in cache for 10 minutes
    retry: (failureCount, error) => {
      // Don't retry on auth errors
      if (error?.message?.includes('organization_id')) return false;
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchOnWindowFocus: false,
    enabled: !!organizationId,
  });
};